import { MAXIMUM_EMBEDDED_TEXT_SIZE } from './constants';
import { DrEntity } from './dr-entity';
import { DrNamedQuery } from './dr-named-query';
import { DrObject, DrObjectType } from './dr-object';
import { ExportStreamOptions } from './export-stream-options';
import { isNullOrUndefined } from './utils';


export class ExportSettings extends DrEntity {
    allExportDatabaseConnections: any;      // used by get, but not set
    documentPrefix: string;
    exportDataAreaHandle: string;
    exportImageExceptions: Array<DrNamedQuery>;
    exportExtensionConversionEnabled: boolean;
    exportVolumesAllowed: boolean;
    folderSize: number;
    exportNearDupeNumericsIncluded: boolean;
    projectHandle: string;                  // used by set, but not get
    selectedExportDatabaseConnection: any;  // used by get, but not set
    exportDatabaseConnectionHandle: string;
    lastUsedDBConnection: any;              // used by get, but not set
    volumePrefix: string;
    postProcessKey: string;                 // this maps to volumeEncryption
    exportSettingsOptions: ExportStreamOptions;
    defaultStartingDocId: number;


    constructor(value?: Partial<ExportSettings>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }
    }
}


export class ExportSettingsViewModel extends DrObject {

    model: Partial<ExportSettings>;
    original: Partial<ExportSettings>;

    constructor(value?: Partial<ExportSettings>) {
        super(DrObjectType.EXPORT_STREAM);
        if (value) {
            this.model = this.deepCopy(value);

            if (this.model['exportSettings']) {
                if (!this.model.exportSettingsOptions) {
                    this.model.exportSettingsOptions = new ExportStreamOptions();
                }
                Object.assign(this.model.exportSettingsOptions, this.model['exportSettings']);
            }

            this.model.postProcessKey = isNullOrUndefined(this.model.postProcessKey) ? '' : value.postProcessKey;
            this.model.defaultStartingDocId = this.model.exportSettingsOptions.startingDocumentId;
        }

        if (this.model) {
            this.original = this.deepCopy(this.model);
        }
    }

    private deepCopy<T>(obj: T): T {
        if (obj === null || typeof obj !== 'object') return obj;
        if (obj instanceof Date) return new Date(obj.getTime()) as any;
        if (obj instanceof Array) return obj.map(item => this.deepCopy(item)) as any;
        
        const copy = {} as T;
        Object.keys(obj).forEach(key => {
            copy[key] = this.deepCopy(obj[key]);
        });
        return copy;
    }

    // note: variables used by the 'get' will not be tested for dirty state
    // see ExportSettings class for 'get' variables.

    isDirty(): boolean {
        // if (this.model.documentPrefix !== this.original.documentPrefix) {
        //     return true;
        // }

        const originalIncludeFullText = this.original.exportSettingsOptions.maximumEmbeddedTextSize === MAXIMUM_EMBEDDED_TEXT_SIZE ? true : false;
        if(this.model.exportSettingsOptions.includeFullText !== originalIncludeFullText) {
            return true;
        }

        if(this.model.exportSettingsOptions.emailOptions !== this.original.exportSettingsOptions.emailOptions) {
            return true;
        }

        if (this.model.exportDataAreaHandle !== this.original.exportDataAreaHandle) {
            return true;
        }

        if (this.model.exportSettingsOptions.exportExtensionConversionEnabled !== this.original.exportSettingsOptions.exportExtensionConversionEnabled) {
            return true;
        }

        if (this.model.exportSettingsOptions.exportVolumesAllowed !== this.original.exportSettingsOptions.exportVolumesAllowed) {
            return true;
        }

        if (this.model.exportSettingsOptions.folderSize !== this.original.exportSettingsOptions.folderSize) {
            return true;
        }

        if (this.model.exportSettingsOptions.exportNearDupeNumericsIncluded !== this.original.exportSettingsOptions.exportNearDupeNumericsIncluded) {
            return true;
        }

        if (this.model.exportSettingsOptions.exportDatabaseConnectionHandle !== this.original.exportSettingsOptions.exportDatabaseConnectionHandle) {
            return true;
        }

        if (this.model.exportSettingsOptions.settingType !== this.original.exportSettingsOptions.settingType) {
            return true;
        }

        if (this.model.exportSettingsOptions.includeAllMasterDuplicates !== this.original.exportSettingsOptions.includeAllMasterDuplicates) {
            return true; 
        }

        // if (this.model.volumePrefix !== this.original.volumePrefix) {
        //     return true;
        // }

        if (this.model.exportSettingsOptions.postProcessKey !== this.original.exportSettingsOptions.postProcessKey) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeSeperateEmailAttachments !== this.original.exportSettingsOptions.includeSeperateEmailAttachments) {
            return true;
        }


        if(this.model.exportSettingsOptions.includeContainerReferences !== this.original.exportSettingsOptions.includeContainerReferences) {
            return true;
        }

        if(this.model.exportSettingsOptions.includePdfOLEChildren !== this.original.exportSettingsOptions.includePdfOLEChildren) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeOLEChildren !== this.original.exportSettingsOptions.includeOLEChildren) {
            return true;
        }

        if(this.model.exportSettingsOptions.removeAttachedArchives !== this.original.exportSettingsOptions.removeAttachedArchives) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeNearDuplicates !== this.original.exportSettingsOptions.includeNearDuplicates) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeThreadAttach !== this.original.exportSettingsOptions.includeThreadAttach) {
            return true;
        }

        // Compare threshold: model is in percentage (80), original may be decimal (0.8) or percentage
        const originalThreshold = this.original.exportSettingsOptions.nearDuplicatesThreshold < 1
            ? this.original.exportSettingsOptions.nearDuplicatesThreshold * 100
            : this.original.exportSettingsOptions.nearDuplicatesThreshold;
        if(this.model.exportSettingsOptions.nearDuplicatesThreshold !== originalThreshold) {
            return true;
        }

        if(this.model.exportSettingsOptions.nearDuplicatesMinTermCount !== this.original.exportSettingsOptions.nearDuplicatesMinTermCount) {
            return true;
        }

        if(this.model.exportSettingsOptions.nearDuplicatesProcessAttachments !== this.original.exportSettingsOptions.nearDuplicatesProcessAttachments) {
            return true;
        }

        if(this.model.exportSettingsOptions.generateConcordance !== this.original.exportSettingsOptions.generateConcordance) {
            return true;
        }

        if(this.model.exportSettingsOptions.generateLst !== this.original.exportSettingsOptions.generateLst) {
            return true;
        }

        if(this.model.exportSettingsOptions.generateDii !== this.original.exportSettingsOptions.generateDii) {
            return true;
        }

        if(this.model.exportSettingsOptions.generateCsv !== this.original.exportSettingsOptions.generateCsv) {
            return true;
        }

        if(this.model.exportSettingsOptions.generateEdrmXml !== this.original.exportSettingsOptions.generateEdrmXml) {
            return true;
        }

        if(this.model.exportSettingsOptions.exportToDb !== this.original.exportSettingsOptions.exportToDb) {
            return true;
        }

        if(this.model.exportSettingsOptions.excludeEmailHeadersInLoadFile !== this.original.exportSettingsOptions.excludeEmailHeadersInLoadFile) {
            return true;
        }

        if(this.model.exportSettingsOptions.separateDupeManifest !== this.original.exportSettingsOptions.separateDupeManifest) {
            return true;
        }

        if(this.model.exportSettingsOptions.overlayManifest !== this.original.exportSettingsOptions.overlayManifest) {
            return true;
        }

        if(this.model.exportSettingsOptions.overlayMetadataTemplateHandle !== this.original.exportSettingsOptions.overlayMetadataTemplateHandle) {
            return true;
        }

        if(this.model.exportSettingsOptions.begAttachMode !== this.original.exportSettingsOptions.begAttachMode) {
            return true;
        }

        if(this.model.exportSettingsOptions.tableName !== this.original.exportSettingsOptions.tableName) {
            return true;
        }

        if(this.model.exportSettingsOptions.maxRecordsPerFile !== this.original.exportSettingsOptions.maxRecordsPerFile) {
            return true;
        }

        if(this.model.exportSettingsOptions.maxManifestRecords !== this.original.exportSettingsOptions.maxManifestRecords) {
            return true;
        }

        if(this.model.exportSettingsOptions.postProcess !== this.original.exportSettingsOptions.postProcess) {
            return true;
        }

        if(this.model.exportSettingsOptions.timezone !== this.original.exportSettingsOptions.timezone) {
            return true;
        }

        if(this.model.exportSettingsOptions.dateFormat !== this.original.exportSettingsOptions.dateFormat) {
            return true;
        }

        const normalizeDelimiter = (value: string) => (value === ' ' || value === " " || value === 'Space') ? 'Space' : value;
        if (normalizeDelimiter(this.model.exportSettingsOptions.dateTimeDelimiter) !== normalizeDelimiter(this.original.exportSettingsOptions.dateTimeDelimiter)) {
            return true;
        }

        if(this.model.exportSettingsOptions.timeFormat !== this.original.exportSettingsOptions.timeFormat) {
            return true;
        }

        if(this.model.exportSettingsOptions.manifestSizeUnits !== this.original.exportSettingsOptions.manifestSizeUnits) {
            return true;
        }

        if(this.model.exportSettingsOptions.concordanceUnicodeFormat !== this.original.exportSettingsOptions.concordanceUnicodeFormat) {
            return true;
        }

        if(this.model.exportSettingsOptions.pageLevelNumbering !== this.original.exportSettingsOptions.pageLevelNumbering) {
            return true;
        }

        if(this.model.exportSettingsOptions.basePath !== this.original.exportSettingsOptions.basePath) {
            return true;
        }

        if(this.model.exportSettingsOptions.volumeLabel !== this.original.exportSettingsOptions.volumeLabel) {
            return true;
        }

        if(this.model.exportSettingsOptions.documentIdPrefix !== this.original.exportSettingsOptions.documentIdPrefix) {
            return true;
        }

        if(this.model.exportSettingsOptions.updatedStartingDocumentId && !isNaN(this.model.exportSettingsOptions.updatedStartingDocumentId) && this.model.defaultStartingDocId !== this.model.exportSettingsOptions.updatedStartingDocumentId) {
            return true;
        }

        if(this.model.exportSettingsOptions.pageSeperator !== this.original.exportSettingsOptions.pageSeperator) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeVolumeLabelInPath !== this.original.exportSettingsOptions.includeVolumeLabelInPath) {
            return true;
        }

        if(this.model.exportSettingsOptions.documentIdPadSize !== this.original.exportSettingsOptions.documentIdPadSize) {
            return true;
        }

        if(this.model.exportSettingsOptions.pagePadSize !== this.original.exportSettingsOptions.pagePadSize) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeNativeFiles !== this.original.exportSettingsOptions.includeNativeFiles) {
            return true;
        }

        if(this.model.exportSettingsOptions.includeTextConversion !== this.original.exportSettingsOptions.includeTextConversion) {
            return true;
        }

        if(this.model.exportSettingsOptions.includePdfConversion !== this.original.exportSettingsOptions.includePdfConversion) {
            return true;
        }

        if(this.model.exportSettingsOptions.nativeSubdirectoryName !== this.original.exportSettingsOptions.nativeSubdirectoryName) {
            return true;
        }
    
        if(this.model.exportSettingsOptions.textSubdirectoryName !== this.original.exportSettingsOptions.textSubdirectoryName) {
            return true;
        }
    
        if(this.model.exportSettingsOptions.pdfSubdirectoryName !== this.original.exportSettingsOptions.pdfSubdirectoryName) {
            return true;
        }

        if(this.model.exportSettingsOptions.emailOutputFormat !== this.original.exportSettingsOptions.emailOutputFormat) {
            return true;
        }

        if(this.model.exportSettingsOptions.excludeEmailHeaders !== this.original.exportSettingsOptions.excludeEmailHeaders) {
            return true;
        }

        if(this.model.exportSettingsOptions.highlightTerms !== this.original.exportSettingsOptions.highlightTerms) {
            return true;
        }

        if(this.model.exportSettingsOptions.convertImagesToPdf !== this.original.exportSettingsOptions.convertImagesToPdf) {
            return true;
        }

        if(this.model.exportSettingsOptions.exportDuplicateMode !== this.original.exportSettingsOptions.exportDuplicateMode) {
            return true;
        }


        // exportImageExceptions - this is handled by the key-value control

        return false;
    }
}

export interface ExportValidation {
    drObject: DrObject;
    volumePrefix: {
        check: () => void,
        invalid: boolean,
        errorMessage: string
    };
    documentPrefix: {
        check: () => void,
        invalid: boolean,
        errorMessage: string
    };
    valid: () => boolean;
}

